type CampaignChannelSelectionFields = {
  can_participant_select_specific_channels?: unknown;
};

type PackageChannel = {
  fk_channel_id?: string | number | null;
  channel_id?: string | number | null;
  creative_id?: string | number | null;
  sys_creative_id?: string | number | null;
  id?: string | number | null;
  channel_name?: string | null;
  creative_name?: string | null;
  channel?: {
    id?: string | number | null;
    channel_id?: string | number | null;
    channel_name?: string | null;
  } | null;
};

type CreativePackage = {
  creative_id?: string | number | null;
  creative_name?: string | null;
  sys_creative_id?: string | number | null;
  package_channels?: PackageChannel[] | null;
};

export type CampaignChannelOption = {
  id: string;
  name: string;
};

export type BlockedPackageSelectionInfo = {
  packageId: string;
  packageName: string;
  channelNames: string[];
  message: string;
};

export type CampaignChannelSelectionData = {
  channels: string[];
  enabledChannels: string[];
  disabledChannels: string[];
  channelData: Array<{
    channel_id: string;
    channel_name: string;
    is_selected: boolean;
  }>;
  enabledCreativeIds: string[];
  disabledCreativeIds: string[];
  creativeChannelData: Array<{
    creative_id: string;
    creative_name: string;
    sys_creative_id: string;
    package_id: string;
    package_name: string;
    channel_id: string;
    channel_name: string;
    is_selected: boolean;
  }>;
};

export type SelectedPackageCreativeData = {
  creative_id: string | number;
  creative_name: string;
  sys_creative_id: string;
};

const TRUE_VALUES = new Set(["1", "true", "yes", "on"]);

function isEnabledValue(value: unknown): boolean {
  if (typeof value === "boolean") {
    return value;
  }

  if (typeof value === "number") {
    return value === 1;
  }

  if (typeof value === "string") {
    return TRUE_VALUES.has(value.trim().toLowerCase());
  }

  return false;
}

export function isParticipantChannelSelectionEnabled(
  campaign?: CampaignChannelSelectionFields | null
): boolean {
  if (!campaign) {
    return false;
  }

  return [campaign.can_participant_select_specific_channels].some(
    isEnabledValue
  );
}

export function getPackageChannelId(channel: PackageChannel): string {
  const id =
    channel.fk_channel_id ??
    channel.channel_id ??
    channel.channel?.id ??
    channel.channel?.channel_id ??
    channel.id ??
    channel.channel_name ??
    channel.channel?.channel_name;

  return id == null ? "" : String(id);
}

function getChannelName(channel: PackageChannel): string {
  return (
    channel.channel_name ||
    channel.channel?.channel_name ||
    getPackageChannelId(channel)
  );
}

export function getPackageChannelOptions(
  creatives: CreativePackage[] = []
): CampaignChannelOption[] {
  const channelsById = new Map<string, CampaignChannelOption>();

  creatives.forEach((creative) => {
    creative.package_channels?.forEach((channel) => {
      const id = getPackageChannelId(channel);

      if (!id || channelsById.has(id)) {
        return;
      }

      channelsById.set(id, {
        id,
        name: getChannelName(channel),
      });
    });
  });

  return Array.from(channelsById.values());
}

export function getDefaultSelectedChannelIds(
  creatives: CreativePackage[] = [],
  selectedPackageIds: Array<string | number> = []
): string[] {
  const selectedIds = new Set(selectedPackageIds.map((id) => String(id)));
  const selectedPackages = creatives.filter((creative) =>
    selectedIds.has(String(creative.creative_id))
  );

  return getPackageChannelOptions(selectedPackages).map(
    (channel) => channel.id
  );
}

function normalizeId(value: string | number | null | undefined): string {
  return value == null ? "" : String(value);
}

function getChannelCreativeId(
  channel: PackageChannel,
  creativePackage: CreativePackage
): string {
  return normalizeId(
    channel.creative_id ??
      channel.sys_creative_id ??
      channel.id ??
      creativePackage.sys_creative_id ??
      creativePackage.creative_id
  );
}

function unique(values: string[]): string[] {
  return Array.from(new Set(values.filter(Boolean)));
}

function splitCreativeIds(value: string): string[] {
  return value
    .split(",")
    .map((id) => id.trim())
    .filter(Boolean);
}

function getCreativePackageChannelIds(
  creativePackage?: CreativePackage
): string[] {
  return unique(
    (creativePackage?.package_channels || []).map((channel) =>
      getPackageChannelId(channel)
    )
  );
}

function getCreativePackageChannelNames(
  creativePackage?: CreativePackage
): string[] {
  return unique(
    (creativePackage?.package_channels || []).map((channel) =>
      getChannelName(channel)
    )
  );
}

function getCreativePackageById(
  creatives: CreativePackage[],
  packageId: string | number
): CreativePackage | undefined {
  return creatives.find(
    (creativePackage) =>
      normalizeId(creativePackage.creative_id) === String(packageId)
  );
}

function getSelectedPackageSysCreativeIds(
  creativePackage: CreativePackage,
  selectedChannelIdSet: Set<string>
): string[] {
  const enabledChannelSysCreativeIds = (
    creativePackage.package_channels || []
  ).flatMap((channel) => {
    const channelId = getPackageChannelId(channel);

    if (!selectedChannelIdSet.has(channelId)) {
      return [];
    }

    const sysCreativeId = normalizeId(channel.sys_creative_id);

    if (sysCreativeId) {
      return splitCreativeIds(sysCreativeId);
    }

    return splitCreativeIds(getChannelCreativeId(channel, creativePackage));
  });

  return unique(enabledChannelSysCreativeIds);
}

export function buildSelectedPackageCreativeData({
  creatives = [],
  selectedPackageIds = [],
  selectedChannelIds = [],
}: {
  creatives?: CreativePackage[];
  selectedPackageIds?: Array<string | number>;
  selectedChannelIds?: Array<string | number>;
}): SelectedPackageCreativeData[] {
  const selectedChannelIdSet = new Set(
    selectedChannelIds.map((id) => String(id))
  );

  return selectedPackageIds
    .map((packageId) => {
      const creativePackage = getCreativePackageById(creatives, packageId);

      if (!creativePackage) {
        return undefined;
      }

      const selectedSysCreativeIds = getSelectedPackageSysCreativeIds(
        creativePackage,
        selectedChannelIdSet
      );

      return {
        creative_id: creativePackage.creative_id ?? packageId,
        creative_name: creativePackage.creative_name || "",
        sys_creative_id: selectedSysCreativeIds.join(","),
      };
    })
    .filter((creativeData): creativeData is SelectedPackageCreativeData =>
      Boolean(creativeData)
    );
}

export function canSelectPackageWithEnabledChannels({
  creatives = [],
  packageId,
  selectedChannelIds = [],
}: {
  creatives?: CreativePackage[];
  packageId: string | number;
  selectedChannelIds?: Array<string | number>;
}): boolean {
  const selectedChannelIdSet = new Set(
    selectedChannelIds.map((channel) => String(channel))
  );
  const packageChannelIds = getCreativePackageChannelIds(
    getCreativePackageById(creatives, packageId)
  );

  return packageChannelIds.some((channelId) =>
    selectedChannelIdSet.has(channelId)
  );
}

export function getBlockedPackageSelectionInfo({
  creatives = [],
  packageId,
}: {
  creatives?: CreativePackage[];
  packageId: string | number;
}): BlockedPackageSelectionInfo {
  const creativePackage = getCreativePackageById(creatives, packageId);
  const packageName = creativePackage?.creative_name || `Package ${packageId}`;
  const channelNames = getCreativePackageChannelNames(creativePackage);
  const channelText = channelNames.length
    ? channelNames.join(", ")
    : "one of this package's channels";

  return {
    packageId: String(packageId),
    packageName,
    channelNames,
    message: `${packageName} cannot be selected because all of its channels are disabled. Enable one of these channels first: ${channelText}.`,
  };
}

export function getChannelsBlockedFromDisabling({
  creatives = [],
  selectedPackageIds = [],
  selectedChannelIds = [],
}: {
  creatives?: CreativePackage[];
  selectedPackageIds?: Array<string | number>;
  selectedChannelIds?: Array<string | number>;
}): string[] {
  const selectedChannelIdSet = new Set(
    selectedChannelIds.map((channel) => String(channel))
  );
  const blockedChannelIds = new Set<string>();

  selectedPackageIds.forEach((packageId) => {
    const packageChannelIds = getCreativePackageChannelIds(
      getCreativePackageById(creatives, packageId)
    );
    const enabledPackageChannelIds = packageChannelIds.filter((channelId) =>
      selectedChannelIdSet.has(channelId)
    );

    if (enabledPackageChannelIds.length === 1) {
      blockedChannelIds.add(enabledPackageChannelIds[0]);
    }
  });

  return Array.from(blockedChannelIds);
}

export function pruneSelectedChannelsForPackages({
  creatives = [],
  selectedPackageIds = [],
  selectedChannelIds = [],
}: {
  creatives?: CreativePackage[];
  selectedPackageIds?: Array<string | number>;
  selectedChannelIds?: Array<string | number>;
}): string[] {
  const normalizedSelectedChannelIds = unique(
    selectedChannelIds.map((channelId) => String(channelId))
  );

  if (!selectedPackageIds.length) {
    return normalizedSelectedChannelIds;
  }

  const selectedPackages = selectedPackageIds
    .map((packageId) => getCreativePackageById(creatives, packageId))
    .filter((creativePackage): creativePackage is CreativePackage =>
      Boolean(creativePackage)
    );
  const remainingPackageChannelIds = unique(
    selectedPackages.flatMap((creativePackage) =>
      getCreativePackageChannelIds(creativePackage)
    )
  );
  const prunedChannelIds = normalizedSelectedChannelIds.filter((channelId) =>
    remainingPackageChannelIds.includes(channelId)
  );

  selectedPackages.forEach((creativePackage) => {
    const packageChannelIds = getCreativePackageChannelIds(creativePackage);
    const hasSelectedPackageChannel = packageChannelIds.some((channelId) =>
      prunedChannelIds.includes(channelId)
    );

    if (!hasSelectedPackageChannel && packageChannelIds[0]) {
      prunedChannelIds.push(packageChannelIds[0]);
    }
  });

  return unique(prunedChannelIds);
}

export function shouldPruneChannelsForPackageChange({
  previousPackageIds = [],
  nextPackageIds = [],
}: {
  previousPackageIds?: Array<string | number>;
  nextPackageIds?: Array<string | number>;
}): boolean {
  const nextPackageIdSet = new Set(nextPackageIds.map((id) => String(id)));

  return previousPackageIds.some((packageId) => {
    return !nextPackageIdSet.has(String(packageId));
  });
}

export function getDraftSelectedChannelIds(draftData?: {
  channels?: unknown;
  enabledChannels?: unknown;
  channelData?: unknown;
}): string[] {
  if (!draftData) {
    return [];
  }

  if (Array.isArray(draftData.channels) && draftData.channels.length) {
    return draftData.channels.map((channel) => String(channel));
  }

  if (
    Array.isArray(draftData.enabledChannels) &&
    draftData.enabledChannels.length
  ) {
    return draftData.enabledChannels.map((channel) => String(channel));
  }

  if (Array.isArray(draftData.channelData)) {
    return draftData.channelData
      .filter((channel) => {
        if (!channel || typeof channel !== "object") {
          return false;
        }

        return Boolean(
          (channel as { is_selected?: unknown; enabled?: unknown })
            .is_selected ?? (channel as { enabled?: unknown }).enabled
        );
      })
      .map((channel) =>
        String(
          (channel as { channel_id?: unknown; id?: unknown }).channel_id ??
            (channel as { id?: unknown }).id
        )
      )
      .filter(Boolean);
  }

  return [];
}

export function buildChannelSelectionData({
  creatives = [],
  selectedPackageIds = [],
  selectedChannelIds = [],
}: {
  creatives?: CreativePackage[];
  selectedPackageIds?: Array<string | number>;
  selectedChannelIds?: Array<string | number>;
}): CampaignChannelSelectionData {
  const selectedPackageIdSet = new Set(
    selectedPackageIds.map((id) => String(id))
  );
  const selectedChannelIdSet = new Set(
    selectedChannelIds.map((id) => String(id))
  );
  const selectedPackages = creatives.filter((creativePackage) =>
    selectedPackageIdSet.has(String(creativePackage.creative_id))
  );
  const channelOptions = getPackageChannelOptions(selectedPackages);
  const selectedChannels = channelOptions
    .filter((channel) => selectedChannelIdSet.has(channel.id))
    .map((channel) => channel.id);
  const disabledChannels = channelOptions
    .filter((channel) => !selectedChannelIdSet.has(channel.id))
    .map((channel) => channel.id);
  const creativeChannelData = selectedPackages.flatMap((creativePackage) =>
    (creativePackage.package_channels || []).map((channel) => {
      const channelId = getPackageChannelId(channel);
      const creativeId = getChannelCreativeId(channel, creativePackage);

      return {
        creative_id: creativeId,
        creative_name:
          channel.creative_name || creativePackage.creative_name || "",
        sys_creative_id: normalizeId(
          channel.sys_creative_id ?? creativePackage.sys_creative_id
        ),
        package_id: normalizeId(creativePackage.creative_id),
        package_name: creativePackage.creative_name || "",
        channel_id: channelId,
        channel_name:
          channel.channel_name || channel.channel?.channel_name || "",
        is_selected: selectedChannelIdSet.has(channelId),
      };
    })
  );

  const enabledCreativeIds = unique(
    creativeChannelData
      .filter((creative) => creative.is_selected)
      .map((creative) => creative.creative_id)
  );
  const disabledCreativeIds = unique(
    creativeChannelData
      .filter((creative) => !creative.is_selected)
      .map((creative) => creative.creative_id)
  );

  return {
    channels: selectedChannels,
    enabledChannels: selectedChannels,
    disabledChannels,
    channelData: channelOptions.map((channel) => ({
      channel_id: channel.id,
      channel_name: channel.name,
      is_selected: selectedChannelIdSet.has(channel.id),
    })),
    enabledCreativeIds,
    disabledCreativeIds,
    creativeChannelData,
  };
}
